import * as React from 'react';
import {useTheme} from '@vebox/etui-system';
import {Div} from '@vebox/etui-system-elements';
import {H5, Icon, icons} from '@vebox/etui-core';
import {keyCodes} from './constants';
import {useAccordionContext} from './context';
import * as S from './styles';
import {DesAccordionSectionProps} from './DesAccordion.types';
import * as focus from './utils';
import {Theme} from "@vebox/etui-themes";

const DesAccordionSection: React.FC<DesAccordionSectionProps> = ({
                                                                     onOpen = null,
                                                                     onClose = null,
                                                                     onToggle = null,
                                                                     styles = {},
                                                                     isOpen: isOpenFromProps,
                                                                     id,
                                                                     title,
                                                                     children,
                                                                     renderHeader,
                                                                     itemProps,
                                                                     renderHeaderFarItem,
                                                                     headerSectionRef,
                                                                     bodySectionRef,
                                                                     containerRef
                                                                 }) => {
    const theme = useTheme() as Theme;
    const {openSections, handleSectionToggle, keepSectionsInDom} =
        useAccordionContext();
    const isOpen = openSections[id];

    React.useEffect(() => {
        if (onOpen && isOpen) {
            onOpen(itemProps);
        }

        if (onClose && !isOpen) {
            onClose(itemProps);
        }
    }, [onOpen, onClose, isOpenFromProps, itemProps, isOpen]);

    const handleClick = () => {
        if (onToggle) {
            onToggle(id);
        } else {
            handleSectionToggle(id);
        }
    };

    const handleKeyPress = (evt: React.KeyboardEvent<HTMLDivElement>): void => {
        const keyCode = evt.which.toString();

        if (keyCode === keyCodes.ENTER || keyCode === keyCodes.SPACE) {
            evt.preventDefault();
            handleClick();
        }

        if (evt.target instanceof HTMLElement) {
            switch (keyCode) {
                case keyCodes.HOME: {
                    evt.preventDefault();
                    focus.focusFirstSiblingOf(evt.target);
                    break;
                }
                case keyCodes.END: {
                    evt.preventDefault();
                    focus.focusLastSiblingOf(evt.target);
                    break;
                }
                case keyCodes.LEFT:
                case keyCodes.UP: {
                    evt.preventDefault();
                    focus.focusPreviousSiblingOf(evt.target);
                    break;
                }
                case keyCodes.RIGHT:
                case keyCodes.DOWN: {
                    evt.preventDefault();
                    focus.focusNextSiblingOf(evt.target);
                    break;
                }
                default: {
                }
            }
        }
    };

    return (
        <S.AccordionSectionDiv isOpen={isOpen} {...styles.root} elRef={containerRef}>
            <S.AccordionSectionHeaderDiv
                onClick={handleClick}
                onKeyDown={handleKeyPress}
                isOpen={isOpen}
                data-component="AccordionSectionHeader"
                {...styles.sectionHeader}
                {...styles.header?.root}
                elRef={headerSectionRef}
            >
                {renderHeader ? (
                    renderHeader(isOpen)
                ) : (
                    <Div display="flex" justifyContent="space-between" width="100%">
                        <Div display="flex" alignItems="center" width="100%">
                            <Div
                                display="flex"
                                alignItems="center"
                                p={`${theme.spacing.sm}px`}
                                color={
                                    isOpen
                                        ? theme.palette.text.primary
                                        : theme.palette.text.secondary
                                }
                                {...styles.header?.expander?.wrapper}
                            >
                                <Icon
                                    glyph={icons.CHEVRON_DOWN}
                                    rotate={isOpen ? 0 : -90}
                                    size={24}
                                    styles={{
                                        root: {transition: '0.2s'},
                                        ...styles.header?.expander?.icon,
                                    }}
                                />
                            </Div>
                            <S.AccordionSectionNameSpan {...styles.header?.title?.wrapper}>
                                <H5
                                    color={
                                        isOpen
                                            ? theme.palette.text.primary
                                            : theme.palette.text.secondary
                                    }
                                    {...styles.header?.title?.text}
                                >
                                    {title}
                                </H5>
                            </S.AccordionSectionNameSpan>
                            {typeof renderHeaderFarItem === 'function' && (
                                <S.AccordionSectionFarItemWrapper {...styles.header?.farItem}>
                                    {renderHeaderFarItem()}
                                </S.AccordionSectionFarItemWrapper>
                            )}
                        </Div>
                    </Div>
                )}
            </S.AccordionSectionHeaderDiv>
            {(isOpen || keepSectionsInDom) && (
                <S.AccordionSectionContentDiv
                    elRef={bodySectionRef}
                    {...styles.sectionContent}
                    isOpen={isOpen}
                    keepSectionContentInDOM={keepSectionsInDom}
                >
                    {children}
                </S.AccordionSectionContentDiv>
            )}
        </S.AccordionSectionDiv>
    );
};

export default DesAccordionSection;
